import { Check } from 'lucide-react';
import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { cn } from '@/lib/utils';

interface Step {
  id: string;
  title: string;
  href: string;
  completed?: boolean;
  disabled?: boolean;
  isActive?: boolean;
}

interface StepNavigationProps {
  steps: Step[];
  className?: string;
  onStepNavigate?: (step: Step) => Promise<boolean | undefined> | boolean | undefined;
  isNavigating?: boolean;
}

export function StepNavigation({
  steps,
  className,
  onStepNavigate,
  isNavigating = false,
}: StepNavigationProps) {
  const navigate = useNavigate();
  const completedStepsCount = steps.filter((step) => step.completed).length;
  const progressPercentage = steps.length > 1 ? (completedStepsCount / steps.length) * 100 : 0;
  const lineOffset = `${50 / steps.length}%`;

  const getStepStatus = (index: number) => {
    const step = steps[index];
    const isActive = step.isActive !== undefined ? step.isActive : false;
    const isCompleted = step.completed && !isActive;

    // Step is clickable if the hook says it's not disabled.
    // All ordering and content gates are handled by the parent hook's disabled property.
    const isClickable = !step.disabled;

    return { isActive, isCompleted, isClickable };
  };

  return (
    <nav className={cn('w-full', className)} aria-label="Progresso das etapas">
      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute top-8 h-0.5 rounded-full bg-gray-200"
          style={{ left: lineOffset, right: lineOffset }}
        >
          <div
            className="h-full rounded-full bg-neutral-900 transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Steps container */}
        <div className="flex items-start justify-between relative">
          {steps.map((step, index) => {
            const { isActive, isCompleted, isClickable } = getStepStatus(index);
            const stepNumber = index + 1;
            const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
              if (!onStepNavigate) return;
              event.preventDefault();
              if (isNavigating || isActive) return;

              try {
                const shouldNavigate = await onStepNavigate(step);
                if (shouldNavigate !== false) navigate(step.href);
              } catch {
                // The owner of the callback is responsible for presenting the error.
              }
            };

            const StepContent = (
              <div key={step.id} className="flex flex-col items-center group relative">
                {/* Step circle */}
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 mb-4 relative z-10 border bg-white shadow-sm',
                    // Completed state
                    isCompleted && 'bg-neutral-900 border-gray-300 text-white',
                    // Active state - outlined with brand ring
                    isActive && 'border-gray-200 text-gray-900 ring-4 ring-blue-100',
                    // Default/inactive state
                    !isCompleted && !isActive && 'border-gray-200 text-gray-400',
                    // Hover effects for clickable steps
                    isClickable && !isActive && !isCompleted && 'hover:border-gray-300',
                    // Keep the background opaque so the connector cannot show through.
                    !isClickable && !isCompleted && !isActive && 'border-gray-100 text-gray-300',
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span>{String(stepNumber).padStart(2, '0')}</span>
                  )}
                </div>

                {/* Step title */}
                <span
                  className={cn(
                    'text-center text-xs font-medium transition-colors duration-200 sm:text-sm',
                    isActive && 'font-semibold text-gray-900',
                    isCompleted && 'text-gray-700',
                    !isCompleted && !isActive && 'text-gray-400',
                  )}
                >
                  {step.title}
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1 h-2 w-2 rounded-full',
                    isActive ? 'bg-neutral-900' : 'invisible',
                  )}
                />
              </div>
            );

            return isClickable ? (
              <Link
                key={step.id}
                to={step.href}
                onClick={(event) => void handleClick(event)}
                className="flex-1 flex justify-center p-2 focus:outline-none"
                aria-current={isActive ? 'step' : undefined}
                aria-disabled={isNavigating}
              >
                {StepContent}
              </Link>
            ) : (
              <div
                key={step.id}
                className="flex-1 flex justify-center cursor-not-allowed p-2"
                title="Complete a etapa anterior"
              >
                {StepContent}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
